import React, { Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { GoogleAnalyticsProvider } from './context/GoogleAnalyticsContext';
import ProtectedRoute from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';

import Navbar from './components/Navbar';
import CookieConsentBanner from './components/CookieConsentBanner';
import VkCommunityChatModal from './components/VkCommunityChatModal';
import usePageTracking from './hooks/usePageTracking';
import { Loader2, MessageSquare } from 'lucide-react';

const VkIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M15.684 2H8.316C4.828 2 2 4.828 2 8.316v7.368C2 19.172 4.828 22 8.316 22h7.368C19.172 22 22 19.172 22 15.684V8.316C22 4.828 19.172 2 15.684 2zM17.47 12.87c.734.717 1.48 1.403 2.19 2.115.353.355.702.716.994 1.12.288.4.398.835.196 1.304-.194.453-.591.605-1.05.613-1.123.02-2.247.012-3.37.012-.66 0-1.17-.306-1.564-.816a8.91 8.91 0 0 0-1.074-1.157c-.206-.2-.416-.396-.683-.497-.372-.14-.68-.035-.86.345-.16.335-.224.698-.24 1.062-.028.618-.4 1.01-1.012 1.054a7.1 7.1 0 0 1-1.468-.027 8.337 8.337 0 0 1-3.666-1.74A9.97 9.97 0 0 1 3.513 11.2a15.7 15.7 0 0 1-1.423-3.692c-.173-.518.067-.93.593-.943 1.134-.03 2.27-.02 3.404-.017.385 0 .684.2.83.565.485 1.206 1.115 2.32 1.933 3.328.16.2.338.388.583.475.29.102.5.012.63-.284.148-.337.214-.694.238-1.056.054-.834-.23-1.352-.865-1.554-.257-.082-.206-.213-.092-.32.484-.45 1.115-.555 1.762-.575 1.053-.03 2.08-.008 3.1.284.453.13.792.413.88 1.034.053.38.03.766-.02 1.144-.06.467-.014.894.276 1.267.143.185.347.24.56.126.315-.17.523-.44.7-.723a13.313 13.313 0 0 0 1.493-3.084c.154-.42.417-.638.868-.616 1.057.05 2.113.02 3.17.02.535 0 .848.33.727.846a14.7 14.7 0 0 1-1.127 3.018 7.424 7.424 0 0 1-.95 1.545c-.328.39-.283.59.088.95z\"/>
  </svg>
);

// Lazy-loaded Page Components with auto-retry and cache-busting logic
const PublicCatalogPage = lazyWithRetry(() => import('./pages/PublicCatalogPage'), 'PublicCatalogPage');
const CatchRegistryPage = lazyWithRetry(() => import('./pages/CatchRegistryPage'), 'CatchRegistryPage');
const CompetitionsPage = lazyWithRetry(() => import('./pages/CompetitionsPage'), 'CompetitionsPage');
const TeamsPage = lazyWithRetry(() => import('./pages/TeamsPage'), 'TeamsPage');
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'), 'LoginPage');
const PersonalCabinetPage = lazyWithRetry(() => import('./pages/PersonalCabinetPage'), 'PersonalCabinetPage');
const AdminDashboardPage = lazyWithRetry(() => import('./pages/AdminDashboardPage'), 'AdminDashboardPage');
const AccessDeniedPage = lazyWithRetry(() => import('./pages/AccessDeniedPage'), 'AccessDeniedPage');
const FaqPage = lazyWithRetry(() => import('./pages/FaqPage'), 'FaqPage');

/**
 * Fallback-заглушка при ленивой загрузке бандла раздела с кнопкой повторной загрузки при задержке
 */
function PageLoader() {
  const [showRetry, setShowRetry] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowRetry(true);
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-emerald-850 px-4 text-center">
      <Loader2 className="w-9 h-9 animate-spin text-emerald-700" />
      <span className="text-sm font-medium text-slate-500">Загрузка раздела...</span>
      {showRetry && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-4 py-2 rounded-xl transition-all shadow-sm active:scale-95"
        >
          Загрузка длится дольше обычного. Нажмите для обновления
        </button>
      )}
    </div>
  );
}

/**
 * Вспомогательный компонент для активации отслеживания просмотров страниц в GA4
 */
function AnalyticsTracker() {
  usePageTracking();
  return null;
}

export default function App() {
  const [isVkChatOpen, setIsVkChatOpen] = useState(false);

  return (
    <ErrorBoundary componentName="AppRoot">
      <AuthProvider>
        <GoogleAnalyticsProvider>
          <BrowserRouter>
            <AnalyticsTracker />
            <div className="min-h-[100dvh] min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans pb-[env(safe-area-inset-bottom)]">
              {/* Main Navigation Header */}
              <Navbar />

              {/* Floating Cookie Consent Banner */}
              <CookieConsentBanner />

              {/* Main Content Area with Suspense for on-demand lazy chunks */}
              <main className="flex-1 w-full pb-16">
                <ErrorBoundary componentName="MainRoutes">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<PublicCatalogPage />} />
                      <Route path="/catches" element={<CatchRegistryPage />} />
                      <Route path="/competitions" element={<CompetitionsPage />} />
                      <Route path="/teams" element={<TeamsPage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/faq" element={<FaqPage />} />
                      <Route path="/storage" element={<Navigate to="/admin?tab=storage" replace />} />
                      <Route path="/403" element={<AccessDeniedPage />} />

                      {/* Protected user routes */}
                      <Route
                        path="/cabinet"
                        element={
                          <ProtectedRoute>
                            <PersonalCabinetPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Protected admin routes */}
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute allowedRoles={['app_admin']}>
                            <AdminDashboardPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Fallback route */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </main>

              {/* Footer */}
              <footer className="w-full bg-slate-100 border-t border-slate-200 py-6 text-center text-xs text-slate-400">
                <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p>© 2026 Carp Arena. Все права защищены.</p>
                  <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 font-semibold text-slate-500">
                    <a href="/faq?category=competitions" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-800 transition-colors">Правила соревнований</a>
                    <a href="/faq" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-800 transition-colors">Справка & ЧаВо (FAQ)</a>
                    <button
                      type="button"
                      onClick={() => setIsVkChatOpen(true)}
                      className="inline-flex items-center gap-1.5 text-slate-600 hover:text-emerald-700 transition-colors cursor-pointer bg-transparent border-none p-0 font-semibold text-xs"
                      title="Написать нам в сообщения сообщества ВКонтакте"
                    >
                      <MessageSquare className="w-4 h-4 text-emerald-600" />
                      <span>Связаться с нами</span>
                    </button>
                    <a 
                      href="https://vk.ru/carparena_ru" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-1.5 hover:text-[#0077FF] transition-colors"
                      title="Официальное сообщество Carp Arena ВКонтакте"
                      aria-label="Мы ВКонтакте"
                    >
                      <VkIcon className="w-4 h-4" />
                      <span>Мы ВКонтакте</span>
                    </a>
                  </div>
                </div>
              </footer>

              {/* VK Community Chat Modal Widget */}
              <VkCommunityChatModal
                isOpen={isVkChatOpen}
                onClose={() => setIsVkChatOpen(false)}
                groupName="Carp Arena"
                groupUrl="https://vk.me/carparena_ru"
              />
            </div>
          </BrowserRouter>
        </GoogleAnalyticsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

